/**
 * The colour a thing wears when it has no logo (design record 2026-09-10,
 * fourth question, option B).
 *
 * Derived from the id rather than assigned, so a thing has a mark the moment it
 * exists and two things are almost never the same colour, which is what makes
 * a palette of forty learnable by sight with nothing uploaded.
 *
 * The cost recorded against that pick was that it spends colour the design
 * system reserves for meaning. This is what stops it: the hue is drawn from a
 * ring that EXCLUDES the status tones' own hues, and the chroma sits below
 * theirs, so a derived mark can never be misread as a success or a warning.
 */

/** Success sits at 163 and 145, warning at 58 and 79, error at 27, primary at
 *  276. A derived hue keeps this far from each of them, in degrees. */
const STATUS_HUES = [27, 58, 79, 145, 163, 276];
const KEEP_AWAY = 18;

/** Below every status tone's chroma (the lowest is warning's 0.1401), so a
 *  derived mark reads as a label rather than as a state. */
const CHROMA = 0.105;

/* The letter sits ON these fills, so the pair is chosen for contrast rather
   than for looks: white on the light fill and near-black on the dark one. A
   mark's letter is small and semi-bold, so it needs the 4.5:1 floor, not the
   3:1 one.
   MEASURED, not argued, by `contrast.test`: over 300 ids the light pair runs
   6.76 to 7.89 and the dark pair 4.92 to 5.48. The theme package's own suite
   cannot check this, because it parses tokens.css and a derived colour does not
   exist there, so moving either number without running that spec is how this
   breaks silently. The dark pair is the tight one: 4.92 leaves 0.42 over the
   floor, so lowering LIGHTNESS_DARK fails it. */
const LIGHTNESS_LIGHT = 45;
const LIGHTNESS_DARK = 62;

/**
 * FNV-1a, 32 bit. A hash rather than an index because ids are uuids with no
 * ordering worth preserving, and this one is short, stable across runtimes,
 * and needs no dependency.
 */
export const hashOf = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
};

/** True when `hue` is within `KEEP_AWAY` degrees of a status tone, the short way
 *  round the circle. */
export const isReservedHue = (hue: number): boolean =>
  STATUS_HUES.some(status => {
    const apart = Math.abs(hue - status) % 360;
    return Math.min(apart, 360 - apart) < KEEP_AWAY;
  });

/**
 * Every hue a derived mark may use, in order.
 *
 * The hash indexes INTO this rather than onto the full circle with a walk out
 * of each reserved band. A walk looks equivalent and is not: roughly three
 * fifths of the circle is reserved, so most ids would land in a band and be
 * pushed to the same handful of band-exit hues, and sixty ids drew only thirty
 * colours. Indexing the allowed space spreads them evenly, which is the entire
 * point of the pick.
 */
const ALLOWED_HUES = Array.from({ length: 360 }, (_, hue) => hue).filter(hue => !isReservedHue(hue));

/** The hue for an id, uniform over the hues no status tone claims. */
export const hueOf = (id: string): number => ALLOWED_HUES[hashOf(id) % ALLOWED_HUES.length] ?? 0;

/** The mark's fill for a thing with no logo. `dark` lifts the lightness so the
 *  same hue carries on the dark rail. */
export const markFill = (id: string, dark: boolean): string => `oklch(${dark ? LIGHTNESS_DARK : LIGHTNESS_LIGHT}% ${CHROMA} ${hueOf(id)})`;

/** The letter a logoless mark draws: the name's first character, uppercased.
 *  Falls back to the id so a thing named with only whitespace still marks. */
export const markInitial = (name: string, id: string): string => {
  const trimmed = name.trim();
  return (trimmed === '' ? id : trimmed).slice(0, 1).toUpperCase();
};
